import type { Prices } from "@b2bportal/core-types";
import { addPrices } from "@b2bportal/core-utilities";
import type { PriceQuoteV2 } from "@b2bportal/lodging-api";
import {
  type FiatPrice,
  Product,
  Payment,
  type FulfillSuccess,
} from "@b2bportal/purchase-api";

import {
  CardPaymentSelectors,
  cartQuoteSelectors,
  LodgingSelectors,
  ParentState,
} from "@hopper-b2b/checkout";
import {
  type ILineItemProps,
  type ISummaryLineItem,
  SummaryLineItemBaseEnum,
} from "@hopper-b2b/types";
import type { DoneInvokeEvent, State } from "xstate";
import type { LodgingMachineContext } from "../types";
import { OfferPaymentState } from "@/modules/checkout/states/Wallet/types";
import { resolveConfirmationEarnPoints } from "@/modules/hotel-checkout/components/ConfirmationPage/resolveConfirmationEarnPoints";
import { HTS_MEDIA_OFFER_NAME_PREFIX } from "@/modules/wallet/WalletOfferBanner/WalletOfferBanner";
import {
  HOTEL_3X_EARN_OFFER_ID,
  HOTEL_3X_EARN_OFFER_NAME,
  HOTEL_DEALS_OFFER_NAME,
  OfferIneligibleReason,
  OfferType,
} from "@/tracking/offers/constants";
import { getOfferFunnelEntrySource } from "@/tracking/offers/offerFunnelEntrySource";
import { getEarnRateByBin } from "@/utilities/avionEarnRates";
import { isEliteOrPremiumTier } from "@/utilities/eliteOrPremiumTier";
import { getPercentOff } from "@/utilities/hotels/discount";
import { getRemainingBalanceAfterRewardsApplied } from "@/utilities/rewards/payment";
import { getFulfilledHotelConfirmationId } from "@/utilities/tracking/fulfilledConfirmationId";

type LodgingCheckoutState = State<LodgingMachineContext>;
type LodgingCheckoutStateWithoutValue = Pick<LodgingCheckoutState, "context">;

/**
 * Amount charged upfront (excludes "Due at Property" / pay-later portion).
 * Points are earned only on the pay-now amount, not on fees paid at the hotel.
 */
export const getLodgingPayNowTotal = (
  state: LodgingCheckoutStateWithoutValue
): { fiat: FiatPrice } | null => {
  const { roomPriceTotal } = getRoomPricing(state);
  const currency = getCurrencyFromPricingInfo(state);

  if (roomPriceTotal && currency) {
    return {
      fiat: {
        ...currency,
        value: roomPriceTotal.value < 0 ? 0 : roomPriceTotal.value,
      },
    };
  }

  // Quote not yet available — fall back to full trip total so the component
  // renders during the shop phase (before a quote exists).
  return getLodgingTotalInPrices(state);
};

export const getLodgingTotalInPrices = (
  state: LodgingCheckoutStateWithoutValue
): { fiat: FiatPrice } | null => {
  const { roomPriceTotalWithTaxesAndFees } = getRoomPricing(state);
  const currency = getCurrencyFromPricingInfo(state);

  if (roomPriceTotalWithTaxesAndFees) {
    const fiatTotal = roomPriceTotalWithTaxesAndFees.value;

    return {
      fiat: {
        ...currency,
        value: fiatTotal < 0 ? 0 : fiatTotal,
      },
    };
  }

  return null;
};

export const getLodgingSummaryLineItems = (
  state: LodgingCheckoutStateWithoutValue
) => {
  const lodgingTotal = getLodgingTotalInPrices(state);

  const lineItems: ISummaryLineItem[] = [];
  if (lodgingTotal) {
    lineItems.push({
      type: SummaryLineItemBaseEnum.TOTAL,
      fiatPrice: lodgingTotal.fiat,
    });
  }

  return lineItems;
};

export const getCurrencyFromPricingInfo = (
  state: LodgingCheckoutStateWithoutValue
) => {
  const shopPricingInfo =
    state.context.lodgingShop.selectedLodging?.price?.tripTotal?.fiat;
  return (
    shopPricingInfo && {
      currencyCode: shopPricingInfo.currencyCode,
      currencySymbol: shopPricingInfo.currencySymbol,
    }
  );
};

export const getShoppedRoomPricing = (
  state: LodgingCheckoutStateWithoutValue
) => {
  const hotelRoomTripTotal =
    state.context.lodgingShop.selectedLodging?.price?.tripTotal?.fiat;

  return {
    roomPriceTotalWithTaxesAndFees: hotelRoomTripTotal,
    roomPriceTotal: undefined,
    roomDueUponCheckInTotal: undefined,
    roomBasePriceTotal: undefined,
    roomBasePricePerNightTotal: undefined,
    roomTaxesAndFeesTotal: undefined,
  };
};

export const getSelectedRoomRate = (
  state: LodgingCheckoutStateWithoutValue
) => {
  const roomInfo = state.context.lodgingShop.selectedRoom;
  const rateId = state.context.lodgingShop.selectedRoomRateId;
  return roomInfo?.products.find((product) => product.rateId?.value === rateId);
};

export const getRoomPricing = (state: LodgingCheckoutStateWithoutValue) => {
  const hotelProduct = LodgingSelectors.getQuotedLodgingProduct(state);
  return hotelProduct
    ? getQuotedRoomPricing(state)
    : getShoppedRoomPricing(state);
};

export const getQuotedRoomPricing = (
  state: LodgingCheckoutStateWithoutValue
) => {
  const quotedProduct = LodgingSelectors.getQuotedLodgingProduct(state);

  if (
    quotedProduct?.LodgingPriceQuote === "PriceQuoteV2" &&
    (quotedProduct as PriceQuoteV2).pricingV2 != null
  ) {
    const v2 = (quotedProduct as PriceQuoteV2).pricingV2!;
    return {
      roomPriceTotal: v2.dueNowTotal?.fiat,
      roomDueUponCheckInTotal:
        v2.dueAtPropertyTotal?.valueInOriginalCurrency?.fiat ??
        v2.dueAtPropertyTotal?.value?.fiat,
      roomPriceTotalWithTaxesAndFees: v2.tripTotal?.fiat,
      roomBasePriceTotal: v2.roomRateTotal?.fiat,
      roomBasePricePerNightTotal: v2.roomRateNightly?.fiat,
      roomTaxesAndFeesTotal: v2.taxesAndFeesDueNow?.total?.fiat,
    };
  }

  const pricing = LodgingSelectors.getQuotedLodgingProductPricingV1(state);
  return {
    roomPriceTotal: pricing?.paymentSchedule?.payNowTotal?.fiat,
    roomDueUponCheckInTotal:
      pricing?.paymentSchedule?.payLaterTotalLocalCurrency?.fiat ??
      pricing?.paymentSchedule?.payLaterTotal?.fiat,
    roomPriceTotalWithTaxesAndFees: pricing?.tripTotal?.fiat,
    roomBasePriceTotal: pricing?.subTotal?.fiat,
    roomBasePricePerNightTotal: pricing?.dailyRate?.fiat,
    roomTaxesAndFeesTotal: pricing?.taxesAndFeesTotal?.fiat,
  };
};
export interface IHotelLineItemProps extends ILineItemProps {
  type?: "base" | "tax" | "due-upon-check-in" | "due-today";
}

export const getLodgingPriceLineItems = (
  state: LodgingCheckoutStateWithoutValue
): IHotelLineItemProps[] => {
  const { roomBasePriceTotal, roomTaxesAndFeesTotal, roomPriceTotal } =
    getRoomPricing(state);

  if (
    roomBasePriceTotal !== undefined &&
    roomTaxesAndFeesTotal !== undefined &&
    roomPriceTotal !== undefined
  ) {
    const lineItems: IHotelLineItemProps[] = [
      {
        baseAmount: roomBasePriceTotal?.value ?? 0,
        type: "base",
        products: undefined,
      },
      {
        baseAmount: roomTaxesAndFeesTotal?.value ?? 0,
        type: "tax",
        products: undefined,
      },
    ];

    return lineItems;
  }
  return [];
};

export const getOtherLodgingPriceLineItems = (
  state: LodgingCheckoutStateWithoutValue
): IHotelLineItemProps[] => {
  const { roomPriceTotal: dueNow, roomDueUponCheckInTotal: dueAtHotel } =
    getRoomPricing(state);

  const lineItems = [];

  if (!dueAtHotel) {
    return;
  }

  if (dueNow) {
    lineItems.push({
      baseAmount: dueNow?.value ?? 0,
      type: "due-today",
      products: undefined,
    });
  }

  if (dueAtHotel) {
    lineItems.push({
      baseAmount: dueAtHotel?.value ?? 0,
      type: "due-upon-check-in",
      products: undefined,
    });
  }

  return lineItems;
};

export const getFailedFintechProducts = (
  state: LodgingCheckoutStateWithoutValue
): Product[] => {
  const error = state.context[ParentState.cartUpdate]?.error;
  const failedProductAdds = error?.data?.failedProductAdds || [];

  return failedProductAdds
    .filter(
      (failedProductAdd) =>
        failedProductAdd.id.product === Product.HtsLodgingCfar
    )
    .map((failedFintechProduct) => failedFintechProduct.id.product);
};

/**
 * Get the card payment amount from the fulfillment response.
 * This is the actual amount charged to the card after rewards are applied.
 * Used on the confirmation screen price breakdown to show the correct "Amount due".
 * Returns $0 when fulfillment exists but there's no card payment (fully covered by rewards).
 */
export const getCardPaymentAmountFromFulfillment = (
  state: LodgingCheckoutStateWithoutValue
): FiatPrice | undefined => {
  const fulfilledPayments =
    state.context[ParentState.cartFulfill]?.fulfilledPayments;

  // If there's no fulfillment yet, return undefined
  if (!fulfilledPayments) {
    return undefined;
  }

  const cardPayment = fulfilledPayments.find((p) => p.type === Payment.Card);
  const tripTotal = getTripTotalFromQuoteBreakdownWithProducts(state);

  // If there's a card payment, return its amount
  if (cardPayment?.value && cardPayment.value.paymentAmount) {
    return {
      value: cardPayment.value.paymentAmount.amount,
      currencyCode: cardPayment.value.paymentAmount.currency,
      currencySymbol:
        cardPayment.value.paymentAmount.currencySymbol ||
        tripTotal?.fiat.currencySymbol,
    };
  }

  // Fallback: get currency from trip total
  if (tripTotal?.fiat) {
    return {
      value: 0,
      currencyCode: tripTotal.fiat.currencyCode,
      currencySymbol: tripTotal.fiat.currencySymbol,
    };
  }

  return undefined;
};

/**
 * Gets CFAR price - either from quote breakdown or from selected offer.
 * Handles cart update scenario where CFAR is selected but not yet in breakdown.
 */
export const getCfarPrice = (
  state: LodgingCheckoutStateWithoutValue
): Prices | undefined => {
  const products = state.context.cartQuote.quoteBreakdown.products;
  const cfarProduct = products?.find(
    (p) => p.product.type === Product.HtsLodgingCfar
  );

  // If CFAR is already in the breakdown, use that price
  if (cfarProduct) {
    return cfarProduct.product.value.totalPremiumAmount;
  }

  // Otherwise, check if CFAR is selected but not yet in breakdown (cart update pending)
  const selectedOffer =
    state.context[ParentState.htsLodgingCfar]?.selectedOffer;
  if (selectedOffer?.offer) {
    return selectedOffer.offer.premiumAmount;
  }

  return undefined;
};

/**
 * Adds pending attached products to a quoted balance.
 * Only adds products that are selected but not yet in the quote breakdown.
 * Used when cart update is pending and fintech products need to be included in totals.
 */
const addPendingAttachedProductsToQuotedBalance = (
  balance: Prices,
  state: LodgingCheckoutStateWithoutValue
): Prices => {
  const products = state.context.cartQuote.quoteBreakdown.products;

  const hasCfarInBreakdown = products?.some(
    (p) => p.product.type === Product.HtsLodgingCfar
  );

  if (hasCfarInBreakdown) {
    return balance;
  }

  const cfarPrice = getCfarPrice(state);

  const isCfarQuotePending = !hasCfarInBreakdown && cfarPrice != null;

  let quoteBalance = balance;

  if (isCfarQuotePending) {
    quoteBalance = addPrices(quoteBalance, cfarPrice);
  }
  return quoteBalance;
};

/**
 * Calculates the total price for a hotel with all products included.
 * On the confirmation page, uses the fulfillment response instead of cart quote.
 * Does not include pay later amount.
 */
export const getTripTotalFromQuoteBreakdownWithProducts = (
  state: LodgingCheckoutStateWithoutValue
): Prices | undefined => {
  const quotedBalance = cartQuoteSelectors.getCartQuotedBalance(state);
  if (!quotedBalance) return undefined;

  // Add any fintech products that are selected but not yet in breakdown (cart update pending)
  return addPendingAttachedProductsToQuotedBalance(quotedBalance, state);
};

interface CompleteBuyTrackingOptions {
  /** True on the poll-failure path; the shared infra also sets `success: false`. */
  purchaseFailed?: boolean;
}

/**
 * Why the 3X offer did not apply, first match wins. The order goes from the
 * broadest gate to the narrowest: a points-only booking has no charged card,
 * so `no_cash_portion` must be reported before `non_rbc_card`.
 */
const getOfferIneligibleReason = ({
  purchaseFailed,
  isOfferLive,
  isTierEligible,
  isCardCharged,
  isRbcCard,
}: {
  purchaseFailed: boolean;
  isOfferLive: boolean;
  isTierEligible: boolean;
  isCardCharged: boolean;
  isRbcCard: boolean | undefined;
}): OfferIneligibleReason | undefined => {
  if (purchaseFailed) return OfferIneligibleReason.failed_purchase;
  if (!isOfferLive) return OfferIneligibleReason.not_launched;
  if (!isTierEligible) return OfferIneligibleReason.tier_ineligible;
  if (!isCardCharged) return OfferIneligibleReason.no_cash_portion;
  if (!isRbcCard) return OfferIneligibleReason.non_rbc_card;
  return undefined;
};

/**
 * FE-computed tracking properties for the hotel_complete_buy event.
 * Backend tracking properties (USD amounts, owed_* payment schedule) are handled by the shared
 * trackPollSuccess infra which extracts them from the quote product's trackingPropertiesV2.
 */
export const getCompleteBuyHotelAdditionalProperties = (
  context: LodgingMachineContext,
  event?: DoneInvokeEvent<FulfillSuccess>,
  { purchaseFailed = false }: CompleteBuyTrackingOptions = {}
): Record<string, string | number | boolean | undefined> => {
  // Get points redeemed from fulfillment payments
  const fulfillRequestPayments =
    context[ParentState.cartFulfill]?.fulfillRequestPayments;
  const rbcPayment = fulfillRequestPayments?.find(
    (p) => p.type === Payment.RbcAvionPoints
  );
  const pointsBreakdown = rbcPayment?.value?.pointsRedemptionBreakdown;
  const total_points_amount = pointsBreakdown?.numberOfFlexiblePoints ?? 0;
  // Rewards/actions.ts adds a card payment only when a balance remains after
  // Avion redemption, so its presence is the "cash portion > 0" signal.
  const isCardCharged =
    fulfillRequestPayments?.some((p) => p.type === Payment.Card) ?? false;

  const entrySource = getOfferFunnelEntrySource();

  // Compose the offers that apply to this booking: 3X when the user is eligible
  // and paying with a card that earns, hotel deals when the purchased hotel
  // has strikethrough pricing.
  const earnRate = context.bonusPointsEarnRate;
  // The bonus needs an offer earn rate, an earn rate on the selected card (the
  // same BIN lookup behind the earn pill on the payment rows and the Avion
  // points summary) and a card actually charged: points earn only on the cash
  // portion, so a points-only booking never carries the 3X offer.
  const selectedCard = CardPaymentSelectors.getSelectedPaymentMethod({
    context,
  });
  const baseEarnRate = getEarnRateByBin(selectedCard?.bin);
  const hasBonusEarn =
    earnRate != null && baseEarnRate != null && isCardCharged;

  // Offer eligibility (RBC-2387): the same signals that drive offer_name, split
  // out so the attach rate can be charted against the eligible population.
  // "RBC card" means a BIN in the Avion earn table; undefined when no card was
  // charged (points only).
  const is_rbc_card = isCardCharged ? baseEarnRate != null : undefined;
  const offer_ineligible_reason = getOfferIneligibleReason({
    purchaseFailed,
    isOfferLive: context.isBonusEarnOfferLive ?? false,
    isTierEligible: isEliteOrPremiumTier(context.sessionInfo),
    isCardCharged,
    isRbcCard: is_rbc_card,
  });
  const offer_eligible = offer_ineligible_reason === undefined;
  // Derive the deal from the room rate actually purchased, not the hotel's
  // preview price — a different rate may carry a different discount.
  const purchasedRate = context.lodgingShop
    ? getSelectedRoomRate({ context })
    : undefined;
  const totalDiscountAware =
    purchasedRate?.totalDiscountAwareTaxesAndFeesIncluded;
  const percentOff = getPercentOff(totalDiscountAware);
  const hasDeal = percentOff > 0;
  // offer_name and offer_type stack the same offers driven by the same signals:
  // 3X when it applies to the selected card, and hotel deals when there is
  // strikethrough pricing.
  // Whether a wallet offer was applied as a payment method, and whether it
  // was an HTS Media (claimable) offer vs. a managed lodging offer.
  const offerPaymentCtx = context[OfferPaymentState];
  const appliedOfferName = offerPaymentCtx?.offerApplied
    ? offerPaymentCtx.selectedOffer?.name
    : undefined;
  const hasWalletOffer = appliedOfferName != null;

  const offerNames = [
    ...(hasBonusEarn ? [HOTEL_3X_EARN_OFFER_NAME] : []),
    ...(hasDeal ? [HOTEL_DEALS_OFFER_NAME] : []),
  ];
  const offerTypes = [
    ...(hasBonusEarn ? [OfferType.rbc_funded_points] : []),
    ...(hasDeal ? [OfferType.hotel_merch] : []),
  ];
  // offer_id: the backend's trackingPropertiesV2 (extracted by trackPollSuccess)
  // already carries the media offer_id / promotion_id. The FE only adds the
  // 3X earn config ID here; the wallet offer's WalletOffer.id is an opaque
  // JWT, not the media UUID, so it is not included.
  const offerIds = [...(hasBonusEarn ? [HOTEL_3X_EARN_OFFER_ID] : [])];
  // is_claimable: only HTS Media offers (claimable via the media claim API)
  // are true. Managed lodging offers and 3X earn are always on (false).
  const is_claimable =
    hasWalletOffer && appliedOfferName.startsWith(HTS_MEDIA_OFFER_NAME_PREFIX);

  // offer_value_cad: actual CAD value of the offer applied to this booking,
  // from the quote breakdown's paymentTotal (the capped discount the BE applied).
  // The strikethrough difference can exceed the cap; the payment reflects the real amount.
  // For rbc_funded_points: needs the card's CAD-per-point redemption rate,
  // which the FE doesn't have. Sends 0 until the backend provides it.
  // TODO(RBC-2641): compute offer_value_cad for rbc_funded_points once
  // the redemption rate is available in the frontend.
  const quoteBreakdown = context[ParentState.cartQuote]?.quoteBreakdown;
  const hasOfferPayment = quoteBreakdown?.payments?.some(
    (p) => p.payment.type === Payment.TravelWalletOffer
  );
  const offer_value_cad = hasOfferPayment
    ? Math.abs(quoteBreakdown?.paymentTotal?.fiat?.value ?? 0)
    : 0;

  // rewards_earn: only when the 3X experiment is active. Uses remainingBalance
  // (card charge after Avion redemption) as the earn base. backendPoints aren't
  // available at tracking time so we pass undefined to resolveConfirmationEarnPoints.
  let rewards_earn: number | undefined;
  if (earnRate != null) {
    const tripTotal = getTripTotalFromQuoteBreakdownWithProducts({ context });
    const awardsCtx = context[ParentState.awardsPayment];
    const remainingBalance = getRemainingBalanceAfterRewardsApplied(
      tripTotal,
      awardsCtx?.rewardsAmountToApply,
      awardsCtx?.rewardsAmountToApplyCashEquivalent
    );
    const fiatTotal = remainingBalance?.value ?? 0;
    const estimatedPoints =
      baseEarnRate != null && fiatTotal > 0
        ? Math.round(fiatTotal * baseEarnRate * earnRate)
        : 0;
    rewards_earn = resolveConfirmationEarnPoints({
      backendPoints: undefined,
      estimatedPoints,
      isEstimateEligible: estimatedPoints > 0,
    });
  }

  return {
    // GA Tag Manager purchase fields. `value` is the GBV as the user saw it:
    // all products before payments, same definition as the reservation's
    // productTotal on the confirmation page.
    confirmation_id: getFulfilledHotelConfirmationId(event),
    value:
      context[ParentState.cartQuote]?.quoteBreakdown?.productTotal?.fiat?.value,
    total_points_amount,
    offer_id: offerIds.length ? offerIds.join(",") : undefined,
    offer_name: offerNames.length ? offerNames.join(",") : undefined,
    offer_type: offerTypes.length ? offerTypes.join(",") : undefined,
    is_claimable,
    offer_value_cad,
    entry_source: entrySource,
    points_multiplier: hasBonusEarn ? earnRate : undefined,
    offer_discount_percentage_value: hasDeal ? percentOff : undefined,
    rewards_earn,
    is_rbc_card,
    offer_eligible,
    offer_ineligible_reason,
  };
};
